import React, { FunctionComponent, ReactNode, useCallback, useEffect, useMemo, useState } from 'react'
import { FormattedMessage } from 'react-intl'
import { useSelector } from 'react-redux'
import { useLocation, useNavigate } from 'react-router'

import { skipToken } from '@reduxjs/toolkit/query'
import { useTheme } from 'styled-components/native'

import {
  getCaregiverContactFromIntakeForm,
  getGuardianInfoForEpisode,
} from '@lyrahealth-inc/shared-app-logic/src/features/appointments/utils'
import { formatPhoneNumberForDisplay } from '@lyrahealth-inc/shared-app-logic/src/features/common/utils/stringUtils'
import { useFlags } from '@lyrahealth-inc/shared-app-logic/src/features/flags/useFlags'
import { isAiConversationType } from '@lyrahealth-inc/shared-app-logic/src/features/messaging/utils'
import { ClientObject } from '@lyrahealth-inc/shared-app-logic/src/models/client/Client'
import { Episode } from '@lyrahealth-inc/shared-app-logic/src/models/episodes/Episodes'
import { CONVERSATION_TYPE } from '@lyrahealth-inc/shared-app-logic/src/models/messages/Messages'
import { ProgramNames } from '@lyrahealth-inc/shared-app-logic/src/models/programs/Programs'
import { BodyText, Size as BodyTextSize } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/bodyText/BodyText'
import { ClientDetails } from '@lyrahealth-inc/ui-core-crossplatform/src/pages/clientDetails/ClientDetails'

import { getEpisodesForDetailsPanel } from './getEpisodesForDetailsPanel'
import { useActionsMenu } from './hooks/useActionsMenu'
import { useAiConversationToggleRows } from './hooks/useAiConversationToggleRows'
import { useEpisodePickerDropdownItems } from './hooks/useEpisodePickerDropdownItems'
import { useShowSidePanel } from './hooks/useShowSidePanel'
import { useTabsConfig } from './hooks/useTabsConfig'
import { RiskBadgeForClient } from '../../../../common/components/riskBadge/RiskBadgeWithPopover'
import { episodeStates } from '../../../../common/constants/appConstants'
import {
  CLIENT_END_OF_SESSION,
  CLIENT_NEW_NOTE,
  CLIENT_NEW_SAFETY_PLAN,
  CLIENT_NOTE,
  CLIENTS_ASSIGNMENT_DETAILS,
  CLIENTS_CONTENT_LIBRARY,
  CLIENTS_NEW_ASSIGNMENT,
  CLIENTS_NEW_SESSION,
  CLIENTS_NEW_SESSION_DETAILS,
} from '../../../../common/constants/routingConstants'
import { useClientRiskFlags } from '../../../../dashboard/openItems/hooks/useClientRiskFlags'
import { getAuthConfig } from '../../../../data/auth/authSelectors'
import { getSelectedClientId } from '../../../../data/lyraTherapy/clientSelectors'
import { useAppDispatch } from '../../../../data/store'
import { JOIN_BANNER_HEIGHT, useAllBannerVisibility } from '../../../../hooks/useAllBannerVisibility'
import { useIsEngageDAProvider } from '../../../../hooks/useIsEngageDAProvider'
import { useIsProviderSessionSticky } from '../../../../hooks/useIsProviderSessionSticky'
import { usePreviousRoute } from '../../../../hooks/usePreviousRoute'
import { useAssignmentsData } from '../../../assignments/data/hooks/useAssignmentsData'
import { clearToastContent, setToastContent } from '../../../data/ltToastAutoActions'
import { useAllEpisodesForSelectedClient } from '../../../episodes/data/hooks/useAllEpisodesForSelectedClient'
import { usePatientConversationsData } from '../../../messages/data/hooks/useConversationsData'
import {
  getClientProfileMessengerTarget,
  getMessagesActiveLiveMsgAppt,
  inLiveMsgSession as inLiveMsgSessionSelector,
} from '../../../messages/data/messagesSelectors'
import { toggleLiveMsgSession } from '../../../messages/data/messagesSlice'
import useDetailsPanelData from '../../../messages/data/useDetailsPanelData'
import { getConversationsUnreadCount } from '../../../messages/data/utils'
import MessagesPopover from '../../../messages/MessagesPopover'
import { useClientAppointmentsData } from '../data/hooks/useClientAppointmentsData'

// Gate ClientDetails with the redesigned header so the banner-visibility timer is not subscribed to
// in the legacy layout.
const ClientDetailsWithJoinBannerOffset: FunctionComponent<React.ComponentProps<typeof ClientDetails>> = (props) => {
  const { hasJoinSessionBannerOpen } = useAllBannerVisibility()
  // When the Join Session banner scrolls away with the page, sticking below it would leave a gap.
  const isProviderSessionSticky = useIsProviderSessionSticky()
  return (
    <ClientDetails
      {...props}
      stickyTopOffset={hasJoinSessionBannerOpen && isProviderSessionSticky ? JOIN_BANNER_HEIGHT : 0}
    />
  )
}

// Create props type that has an outlet prop that is a react child node
type RedesignedClientProps = {
  content: React.ReactElement
  userId: string
  canReferCare: boolean
  canEndEpisode: boolean
  goToNewNote: () => void
  isEndEpisodeButtonVisible: boolean
  endEpisode: () => void
  modalProps: {
    modalContents: ReactNode
    onRequestClose: () => void
    onCloseEnd: () => void
  }
  clientDetails: ClientObject
  setClientDetailsSelectedEpisode: (selectedEpisode: Episode) => void
  currentEpisode: Episode | undefined
  selectedEpisodeId?: string
  setForceMessagingHubToOpenPopover: React.Dispatch<React.SetStateAction<boolean>>
  forceMessagingHubToOpenPopover: boolean
  contentHasNoMargin?: boolean
  showSubNav: boolean
  appLocked?: boolean
  onExportClientRecords?: () => void
  clientProfileHeaderRef: React.ComponentProps<typeof ClientDetails>['containerRef']
  isClientProfileHeaderCollapsed: boolean
  clientProfileHeaderCompensationHeight: number
}

export const RedesignedClient: FunctionComponent<RedesignedClientProps> = ({
  content,
  userId,
  canReferCare,
  canEndEpisode,
  goToNewNote,
  isEndEpisodeButtonVisible,
  endEpisode,
  modalProps,
  clientDetails,
  setClientDetailsSelectedEpisode,
  selectedEpisodeId,
  setForceMessagingHubToOpenPopover,
  forceMessagingHubToOpenPopover,
  contentHasNoMargin,
  currentEpisode,
  showSubNav,
  appLocked,
  onExportClientRecords,
  clientProfileHeaderRef,
  isClientProfileHeaderCollapsed,
  clientProfileHeaderCompensationHeight,
}) => {
  const location = useLocation()
  const navigate = useNavigate()
  const [entryPoint, setEntryPoint] = useState<string | null>(null)
  const [toastText, setToastText] = useState<string | undefined>(undefined)
  const tabs = useTabsConfig(currentEpisode)
  const showSidePanel = useShowSidePanel()

  const { isClientHeaderRedesignEnabled, isDBTTeensEnabled, isSafetyPlanEnabled } = useFlags()
  const config = useSelector(getAuthConfig)
  const openClientAccessSupported = config?.openClientAccessSupported ?? false
  const isDBTTeensProgram = currentEpisode?.program_name === ProgramNames.DialecticalBehaviorTherapyTeens
  const FULLSCREEN_ROUTES = useMemo(
    () =>
      new Set([
        CLIENTS_ASSIGNMENT_DETAILS.route,
        CLIENT_END_OF_SESSION.route,
        CLIENT_NOTE.route,
        CLIENT_NEW_NOTE.route,
        CLIENT_NEW_SAFETY_PLAN.route,
        ...(isDBTTeensProgram ? [CLIENTS_CONTENT_LIBRARY.route, CLIENTS_NEW_ASSIGNMENT.route] : []),
        ...(isClientHeaderRedesignEnabled ? [CLIENTS_NEW_SESSION.route, CLIENTS_NEW_SESSION_DETAILS.route] : []),
      ]),
    [isDBTTeensProgram, isClientHeaderRedesignEnabled],
  )
  const isFullscreenRoute = FULLSCREEN_ROUTES.has(location.pathname)

  const dropdownOption = useActionsMenu(
    goToNewNote,
    canReferCare,
    userId,
    setToastText,
    isEndEpisodeButtonVisible,
    endEpisode,
    canEndEpisode,
    clientDetails,
    onExportClientRecords,
  )
  const { data: episodes = [] } = useAllEpisodesForSelectedClient()
  const { data: appointments = [] } = useClientAppointmentsData()
  const { data: assignments } = useAssignmentsData()

  const episodesForDetailsPanel = useMemo(
    () =>
      getEpisodesForDetailsPanel({
        episodes,
        currentEpisode,
        providerId: userId,
        isDBTTeensEnabled,
        openClientAccessSupported,
      }),
    [currentEpisode, episodes, isDBTTeensEnabled, openClientAccessSupported, userId],
  )
  const isEngageDAProvider = useIsEngageDAProvider()

  const episodePickerDropdownItems = useEpisodePickerDropdownItems(
    episodes,
    setClientDetailsSelectedEpisode,
    currentEpisode,
  )

  const atRiskClientIds = useClientRiskFlags()
  const clientDetailsFromHook = useDetailsPanelData({
    client: clientDetails,
    episodes: episodesForDetailsPanel,
    isEngageDAProvider,
    appointments,
    assignments,
    hasClientSelfHarmTask: atRiskClientIds.has(clientDetails.id),
  })

  const clientDetailsForPanel = useMemo(() => {
    if (!clientDetailsFromHook) {
      return null
    }

    const intakeContact = getCaregiverContactFromIntakeForm(assignments, currentEpisode?.id)
    const participantGuardianInfo = getGuardianInfoForEpisode([], currentEpisode?.participants, {
      includeSecondaryPatientAsGuardian: isDBTTeensProgram,
    })
    const guardianEmail = participantGuardianInfo.guardianEmail || ''
    const guardianPhone = participantGuardianInfo.guardianPhone || ''
    const formattedGuardianPhone = formatPhoneNumberForDisplay(guardianPhone) || guardianPhone

    if (!guardianEmail && !guardianPhone) {
      return clientDetailsFromHook
    }

    return {
      ...clientDetailsFromHook,
      guardian: {
        ...clientDetailsFromHook.guardian,
        ...(guardianEmail && !intakeContact.email && { email: guardianEmail }),
        ...(guardianPhone && !intakeContact.phone && { phone: formattedGuardianPhone }),
      },
    }
  }, [assignments, clientDetailsFromHook, currentEpisode?.id, currentEpisode?.participants, isDBTTeensProgram])

  const { colors } = useTheme()

  const caregiverSubtitle = useMemo(() => {
    if (!isClientHeaderRedesignEnabled) {
      return undefined
    }
    const guardian = clientDetailsForPanel?.guardian
    const guardianName = [guardian?.firstName, guardian?.lastName].filter(Boolean).join(' ')
    if (!guardianName) {
      return undefined
    }
    return (
      <BodyText
        size={BodyTextSize.DEFAULT}
        text={
          <FormattedMessage
            defaultMessage='<secondary>with caregiver support from</secondary> {guardianName}'
            description='Subtitle on the client header naming the supporting caregiver'
            values={{
              guardianName,
              secondary: (chunks) => (
                <BodyText size={BodyTextSize.DEFAULT} color={colors.textSecondary} text={chunks} />
              ),
            }}
          />
        }
      />
    )
  }, [clientDetailsForPanel, colors.textSecondary, isClientHeaderRedesignEnabled])

  const clientId = useSelector(getSelectedClientId)
  const inLiveMsgSession = useSelector(inLiveMsgSessionSelector)
  const activeLiveMsgAppt = useSelector(getMessagesActiveLiveMsgAppt)

  const dispatch = useAppDispatch()

  const prevRoute = usePreviousRoute()

  const { currentData: conversations } = usePatientConversationsData(
    clientId ? { patientId: clientId, providerId: userId, types: CONVERSATION_TYPE.PROVIDER } : skipToken,
  )
  const numUnreadMessages = getConversationsUnreadCount(
    (conversations ?? []).filter((conversation) => !isAiConversationType(conversation.conversation_attributes?.type)),
  )

  useEffect(() => {
    setEntryPoint(prevRoute ?? null)
    // Only want to track the prevRoute the first time client details is rendered because there are
    // other internal routes that will overwrite the previous route and we will then lose the entry point.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (toastText) {
      dispatch(
        setToastContent({
          text: toastText,
          id: 'copy-id-to-clipboard-toast',
          toastType: 'success',
        }),
      )
    }
    return () => {
      clearToastContent()
    }
  }, [toastText, dispatch])

  // A Care services provider target opens their conversation, not the live session.
  const opensProviderConversation = useSelector(getClientProfileMessengerTarget)?.clientId === clientId
  const handleMessagesPopoverOpen = useCallback(() => {
    if (!inLiveMsgSession && !opensProviderConversation && clientId === activeLiveMsgAppt?.userId) {
      dispatch(toggleLiveMsgSession(true))
    }
    setForceMessagingHubToOpenPopover(false)
  }, [
    activeLiveMsgAppt?.userId,
    clientId,
    dispatch,
    inLiveMsgSession,
    opensProviderConversation,
    setForceMessagingHubToOpenPopover,
  ])

  const aiConversationToggleRows = useAiConversationToggleRows({
    currentEpisode,
    clientDetails,
    clientId,
    providerId: userId,
    skip: isClientHeaderRedesignEnabled,
  })

  if (isFullscreenRoute) {
    return <>{content}</>
  }

  const handleBack = () => {
    if (entryPoint) {
      navigate(entryPoint)
    } else {
      navigate(-1)
    }
  }

  const ClientDetailsComponent = isClientHeaderRedesignEnabled ? ClientDetailsWithJoinBannerOffset : ClientDetails

  return (
    <>
      <ClientDetailsComponent
        tabs={tabs}
        content={content}
        onBackPress={appLocked ? undefined : handleBack}
        dropdownOption={dropdownOption}
        modalProps={modalProps}
        clientDetails={clientDetailsForPanel}
        messagingPopover={MessagesPopover}
        forceMessagingHubToOpenPopover={forceMessagingHubToOpenPopover}
        onMessagesPopoverOpen={handleMessagesPopoverOpen}
        numUnreadMessages={numUnreadMessages}
        episodePickerDropdownItems={episodePickerDropdownItems}
        selectedEpisodeId={selectedEpisodeId}
        contentHasNoMargin={contentHasNoMargin || showSidePanel}
        showSubNav={showSubNav}
        showExportButton={!!onExportClientRecords}
        onExportClientRecords={onExportClientRecords}
        aiConversationToggleRows={aiConversationToggleRows}
        showEmail={isEngageDAProvider}
        useHorizontalHeader={isClientHeaderRedesignEnabled}
        caregiverSubtitle={caregiverSubtitle}
        riskBadgeSlot={
          !config?.hideRiskAlerts && clientId ? (
            <RiskBadgeForClient
              clientId={clientId}
              logNoteSource='clientProfile'
              showRiskBadge={clientDetailsForPanel?.client.showRiskBadge}
              isClientChartVersion={isSafetyPlanEnabled}
            />
          ) : undefined
        }
        hideScheduleSessionButton={currentEpisode?.state !== episodeStates.IN_PROGRESS}
        onSchedulePress={
          currentEpisode?.state === episodeStates.IN_PROGRESS
            ? () => navigate(CLIENTS_NEW_SESSION.route, { state: { scheduleSessionOrigin: location.pathname } })
            : undefined
        }
        containerRef={clientProfileHeaderRef}
        isCollapsed={isClientProfileHeaderCollapsed}
        compensationHeight={clientProfileHeaderCompensationHeight}
      />
    </>
  )
}
